/**
 * 统计仪表板页面
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Typography, Spin, message, Table } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  ApiOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { getStatsOverview, StatsOverview } from '../api/users';
import type { ColumnsType } from 'antd/es/table';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsOverview | null>(null);

  /**
   * 加载统计数据
   */
  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await getStatsOverview();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('加载统计数据失败:', error);
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  /**
   * 国家分布表格列
   */
  const countryColumns: ColumnsType<any> = [
    {
      title: '排名',
      key: 'rank',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: '国家',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: '用户数',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
  ];

  /**
   * 版本分布表格列
   */
  const versionColumns: ColumnsType<any> = [
    {
      title: '排名',
      key: 'rank',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'App 版本',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: '用户数',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
    },
  ];

  if (loading || !stats) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>数据统计</Title>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总用户数"
              value={stats.total_users}
              prefix={<TeamOutlined />}
              valueStyle={{ color: 'var(--admin-success)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="24小时活跃"
              value={stats.active_users_today}
              prefix={<UserOutlined />}
              valueStyle={{ color: 'var(--admin-primary)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="30天活跃"
              value={stats.active_users_this_month}
              prefix={<UserOutlined />}
              valueStyle={{ color: 'var(--admin-primary)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="近2天请求"
              value={stats.total_requests_this_month}
              prefix={<ApiOutlined />}
              valueStyle={{ color: 'var(--admin-purple)' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="封禁用户"
              value={stats.blocked_users}
              prefix={<StopOutlined />}
              valueStyle={{ color: stats.blocked_users > 0 ? 'var(--admin-error)' : 'var(--admin-secondary)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="平均请求/用户"
              value={
                stats.total_users > 0
                  ? (stats.total_requests_this_month / stats.active_users_this_month).toFixed(1)
                  : 0
              }
              suffix="次"
              valueStyle={{ color: 'var(--admin-warning)' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 分布统计 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="国家分布 Top 5">
            <Table
              columns={countryColumns}
              dataSource={stats.top_countries}
              rowKey="country"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="App 版本分布 Top 5">
            <Table
              columns={versionColumns}
              dataSource={stats.top_versions}
              rowKey="version"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
